/**
 * The Tonight stream, as a pure function (proposal "Tonight, the feed";
 * docs/screens.md §`/`). Chronological, not ranked:
 *
 * 1. Friend items — `/v1/friends/activity` (binge-grouped with the shared
 *    `groupActivityRows`; an event carrying the additive `rating` becomes a
 *    `rated` card) and `/v1/community/feed/following` (`commented`) — merged
 *    newest first by timestamp.
 * 2. Dedupe: one activity card per friend × title per 6 h. A `rated` card
 *    wins over a plain watch or a binge group in the same window; a binge
 *    group wins over a single watch. Comments are never dropped for a watch
 *    (they carry the friend's own words) — they only dedupe by comment id.
 * 3. Paging watermark: while a source still has pages, friend items older
 *    than that source's oldest loaded item are deferred, so loading the next
 *    page appends below instead of inserting above what is already on screen.
 * 4. Held items: friend cards newer than `holdNewerThan` are counted, not
 *    rendered — they wait behind the "N new" pill so the list never shifts
 *    while someone reads.
 * 5. Capped inserts at fixed slots, so earlier cards never move when more
 *    load: a `hot` card (trending on your shelf) at every slot ≡ 5 (mod 6) —
 *    at most one per six items — and the single `because` card at slot 3
 *    (never in the first three; one per stream, so ≤ 1 per ten). When both
 *    friend sources are exhausted, one more hot (if none in the last five)
 *    and the because card (if at least three items precede it) close the
 *    stream — which is also how a no-friends feed still shows a hot card.
 */

import type { CommunityFeedItem, Title } from '@shared/types';

import type { FriendActivityItem } from '@/features/friends/api';
import { groupActivityRows, newestWatchedAt, type ActivityRow } from '@/features/friends/feed';

export const DEDUPE_WINDOW_MS = 6 * 60 * 60 * 1000;
/** A hot card lands on every slot where `index % HOT_EVERY === HOT_SLOT`. */
export const HOT_EVERY = 6;
export const HOT_SLOT = 5;
/** The because card's slot — the fourth position, never the first three. */
export const BECAUSE_SLOT = 3;

export interface BecauseSource {
  /** The user's most recently completed title. */
  source: Title;
  /** Similar titles, already filtered and capped by the caller. */
  titles: Title[];
}

export interface FeedSources {
  activity: FriendActivityItem[];
  comments: CommunityFeedItem[];
  hot: CommunityFeedItem[];
  because?: BecauseSource | null;
  /** The activity source has another page to load. */
  activityHasMore?: boolean;
  /** The following-comments source has another page to load. */
  commentsHasMore?: boolean;
  /** Friend cards strictly newer than this (epoch ms) are held back. */
  holdNewerThan?: number | null;
  /** Trending comment ids to leave out of the stream (shown elsewhere). */
  excludeHotIds?: ReadonlySet<string>;
}

export type FeedCard =
  | { kind: 'watched'; key: string; at: string; row: ActivityRow<FriendActivityItem> }
  | { kind: 'rated'; key: string; at: string; item: FriendActivityItem }
  | { kind: 'commented'; key: string; at: string; item: CommunityFeedItem }
  | { kind: 'hot'; key: string; item: CommunityFeedItem }
  | { kind: 'because'; key: string; source: Title; titles: Title[] };

export type FriendCard = Extract<FeedCard, { kind: 'watched' | 'rated' | 'commented' }>;

export interface FeedModel {
  cards: FeedCard[];
  /** Friend cards held back behind the "N new" pill. */
  held: number;
  /** Newest friend-card timestamp seen (held or not), epoch ms; null when none. */
  newest: number | null;
}

function parse(iso: string): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

/** Card time, clamped to `now` so a skewed future timestamp can't pin to the top. */
function timeOf(card: FriendCard, now: number): number {
  return Math.min(parse(card.at), now);
}

function friendIdOf(card: FriendCard): string | undefined {
  if (card.kind === 'commented') return card.item.friend?.publicUserId ?? card.item.comment.author?.publicUserId;
  const item = card.kind === 'rated' ? card.item : card.row.kind === 'group' ? card.row.items[0] : card.row.item;
  return item.friend?.publicUserId;
}

function titleIdOf(card: FriendCard): string {
  if (card.kind === 'commented') return card.item.title.id;
  const item = card.kind === 'rated' ? card.item : card.row.kind === 'group' ? card.row.items[0] : card.row.item;
  return item.title.id;
}

/** Dedupe priority inside one friend × title window: rated > binge > watch. */
function priority(card: FriendCard): number {
  if (card.kind === 'rated') return 3;
  if (card.kind === 'watched' && card.row.kind === 'group') return 2;
  return 1;
}

function byTimeDesc(now: number) {
  return (a: FriendCard, b: FriendCard) => {
    const diff = timeOf(b, now) - timeOf(a, now);
    if (diff !== 0) return diff;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  };
}

function activityCards(activity: FriendActivityItem[]): FriendCard[] {
  // Newest first before grouping (pages from two fetches may interleave).
  const sorted = activity
    .map((item, index) => ({ item, index }))
    .sort((a, b) => parse(b.item.watchedAt) - parse(a.item.watchedAt) || a.index - b.index)
    .map(({ item }) => item);
  return groupActivityRows(sorted).map((row): FriendCard => {
    if (row.kind === 'group') {
      return { kind: 'watched', key: `g:${row.items[0].id}`, at: newestWatchedAt(row.items), row };
    }
    if (row.item.rating !== undefined) {
      return { kind: 'rated', key: `r:${row.item.id}`, at: row.item.watchedAt, item: row.item };
    }
    return { kind: 'watched', key: `w:${row.item.id}`, at: row.item.watchedAt, row };
  });
}

function dedupeActivity(cards: FriendCard[], now: number): FriendCard[] {
  const kept: FriendCard[] = [];
  const dropped = new Set<FriendCard>();
  for (const card of [...cards].sort(byTimeDesc(now))) {
    const friend = friendIdOf(card);
    if (!friend) {
      kept.push(card);
      continue;
    }
    const key = `${friend}|${titleIdOf(card)}`;
    const rival = kept.find(
      (k) =>
        !dropped.has(k) &&
        `${friendIdOf(k)}|${titleIdOf(k)}` === key &&
        Math.abs(timeOf(k, now) - timeOf(card, now)) <= DEDUPE_WINDOW_MS,
    );
    if (!rival) {
      kept.push(card);
    } else if (priority(card) > priority(rival)) {
      dropped.add(rival);
      kept.push(card);
    }
  }
  return kept.filter((card) => !dropped.has(card));
}

function commentCards(comments: CommunityFeedItem[]): FriendCard[] {
  const seen = new Set<string>();
  const cards: FriendCard[] = [];
  for (const item of comments) {
    const id = item.comment.commentId;
    if (seen.has(id) || item.comment.body === null) continue;
    seen.add(id);
    cards.push({ kind: 'commented', key: `c:${id}`, at: item.comment.createdAt, item });
  }
  return cards;
}

function oldest(cards: FriendCard[], now: number): number | null {
  if (cards.length === 0) return null;
  return Math.min(...cards.map((c) => timeOf(c, now)));
}

export function buildFeed(sources: FeedSources, now: number): FeedModel {
  const activity = dedupeActivity(activityCards(sources.activity), now);
  const comments = commentCards(sources.comments);
  let friends = [...activity, ...comments].sort(byTimeDesc(now));

  const newest = friends.length > 0 ? timeOf(friends[0], now) : null;

  // Paging watermark — defer what a still-loading source could still precede.
  const floors = [
    sources.activityHasMore ? oldest(activity, now) : null,
    sources.commentsHasMore ? oldest(comments, now) : null,
  ].filter((f): f is number => f !== null);
  if (floors.length > 0) {
    const floor = Math.max(...floors);
    friends = friends.filter((card) => timeOf(card, now) >= floor);
  }

  // New since the reader's cutoff: counted, not rendered.
  let held = 0;
  if (sources.holdNewerThan !== undefined && sources.holdNewerThan !== null) {
    const cutoff = sources.holdNewerThan;
    held = friends.filter((card) => timeOf(card, now) > cutoff).length;
    friends = friends.filter((card) => timeOf(card, now) <= cutoff);
  }

  // Hot cards: never repeat a friend comment already in the stream.
  const friendCommentIds = new Set(comments.map((c) => (c.kind === 'commented' ? c.item.comment.commentId : '')));
  const seenHot = new Set<string>();
  const hot = sources.hot.filter((item) => {
    const id = item.comment.commentId;
    if (seenHot.has(id) || friendCommentIds.has(id) || sources.excludeHotIds?.has(id)) return false;
    if (item.comment.body === null) return false;
    seenHot.add(id);
    return true;
  });

  const because =
    sources.because && sources.because.titles.length > 0 ? sources.because : null;
  const becauseCard: FeedCard | null = because
    ? { kind: 'because', key: `b:${because.source.id}`, source: because.source, titles: because.titles }
    : null;

  const cards: FeedCard[] = [];
  let fi = 0;
  let hi = 0;
  let becausePlaced = false;
  const pushHot = () => {
    const item = hot[hi++];
    cards.push({ kind: 'hot', key: `h:${item.comment.commentId}`, item });
  };

  for (;;) {
    const slot = cards.length;
    if (becauseCard && !becausePlaced && slot === BECAUSE_SLOT) {
      cards.push(becauseCard);
      becausePlaced = true;
      continue;
    }
    if (slot % HOT_EVERY === HOT_SLOT && hi < hot.length) {
      pushHot();
      continue;
    }
    if (fi < friends.length) {
      cards.push(friends[fi++]);
      continue;
    }
    break;
  }

  const exhausted = !sources.activityHasMore && !sources.commentsHasMore;
  if (exhausted) {
    let lastHot = -1;
    cards.forEach((card, index) => {
      if (card.kind === 'hot') lastHot = index;
    });
    if (hi < hot.length && (lastHot === -1 || cards.length - lastHot >= HOT_EVERY)) pushHot();
    if (becauseCard && !becausePlaced && cards.length >= BECAUSE_SLOT) {
      cards.push(becauseCard);
      becausePlaced = true;
    }
  }

  return { cards, held, newest };
}
